import React, { useState, useEffect } from 'react';
import "./Calendar.css";
import BodyExtraSmallMedium from '../designSystem/BodyExtraSmallMedium';
import BodySmallMedium from '../designSystem/BodySmallMedium';
import MarkLeaveModal from './MarkLeaveModal';
import { useDispatch } from 'react-redux';
import { addEmployeePlan, updateEmployeePlan } from '../../actions/userActions/EmployeePlanAction';
import { Calendar, Info } from 'lucide-react';

const CalendarMonthView = ({ days, planDates, onDayClick, planStatus, planId, myPlan, copyMode, onCopySelectionChange,isPlannerOpen }) => {
    const DRAFT = 1;
    const NEEDSREVISION = 4;
    const APPROVED = 3;
    const [showMenu, setShowMenu] = useState(null);
    const [isMarkLeaveModalVisible, setIsMarkLeaveModalVisible] = useState(false);
    const [selectedDay, setSelectedDay] = useState(null);
    const [showTooltip, setShowTooltip] = useState(null);
    const HEADOFFICE = "Head Office";
    const permissions = JSON.parse(localStorage.getItem('permissionsList'));
    const dispatch = useDispatch();
    const [selectedCopyDays, setSelectedCopyDays] = useState([]);

    useEffect(() => {
  if (onCopySelectionChange) {
    onCopySelectionChange(selectedCopyDays);
  }
}, [selectedCopyDays]);

useEffect(() => {
  if (!copyMode) {
    setSelectedCopyDays([]); // reset when exiting copy mode
  }
}, [copyMode]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (showMenu && !event.target.closest('.three-dots-menu')) {
                setShowMenu(null);
            }
        };

        if (showMenu) {
            document.addEventListener('click', handleClickOutside);
        }

        return () => {
            document.removeEventListener('click', handleClickOutside);
        };
    }, [showMenu]);

    // Limit display to 3 doctor names max per calendar cell
    const MAX_VISIBLE_DOCTORS = 2;

    // Function to find ALL plan dates that match the given day (across all visit profiles)
// Function to find ALL plan dates that match the given day (across all visit profiles)
const getEventsForDay = (day) => {
    if (!planDates || planDates.length === 0) return null;
    
    const matchingPlans = planDates.filter(plan => {
        const planDate = new Date(plan.planDate);
        return (
            day.isCurrentMonth &&
            planDate.getDate() === day.day &&
            planDate.getMonth() + 1 === day.month &&
            planDate.getFullYear() === day.year
        );
    });
    
    if (!matchingPlans || matchingPlans.length === 0) return null;
    
    // Return array of all matching plans for this day
    return matchingPlans;
};

    const getAddButton = (day, events) => {
    if (!myPlan || (planStatus !== DRAFT && planStatus !== NEEDSREVISION) || !Array.isArray(permissions) || !permissions.includes('CanAddPlan')) {
        return false;
    }
    if (!day.isCurrentMonth || isDateNotValid(day)) {
        return false;
    }
    
    // Handle both single event and array of events
    if (events) {
        if (Array.isArray(events)) {
            // If any event has plan details, don't show add button
            const hasAnyPlanDetails = events.some(e => e?.planDetails?.length > 0);
            if (hasAnyPlanDetails) return false;
        } else if (events?.planDetails?.length > 0) {
            return false;
        }
    }
    
    return isPlannerOpen(day);
}

    const handleOnDayClick = (day) => {
    const events = getEventsForDay(day);
    const updatedDay = { ...day, employeePlanId: planId };
    
    // If events is an array, pass it as-is for ViewDrawer
    // If it's a single event, wrap it in an array for consistency
    const formattedEvents = Array.isArray(events) ? events : (events ? [events] : null);
    
    onDayClick(updatedDay, formattedEvents, planStatus, myPlan);
};

const isDateNotValid = (day) => {
  const currentDate = new Date();
  const providedDate = new Date(day.year, day.month - 1, day.day);
  if (
    day.year === 2026 && 
    day.month === 1 && 
    currentDate.getFullYear() === 2026 &&
    currentDate.getMonth() === 0 && 
    currentDate.getDate() <= 10
  ) {
    return false; 
  }
    return providedDate <= currentDate;
};

    const isPastDate = (day) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0); 
        const providedDate = new Date(day.year, day.month - 1, day.day);
        return providedDate < today;
        };


    const handleThreeDotsClick = (e, day, events) => {
        e.preventDefault();
        e.stopPropagation();
        const dayKey = `${day.year}-${day.month}-${day.day}`;
        setShowMenu(showMenu === dayKey ? null : dayKey);
    };

    const handleMarkLeaveClick = (e, day, events) => {
        e.preventDefault();
        e.stopPropagation();
        setSelectedDay({ day, events });
        setIsMarkLeaveModalVisible(true);
        setShowMenu(null);
    };
    const closeMarkLeaveModal = () => {
        setIsMarkLeaveModalVisible(false);
        setSelectedDay(null);
    };

   const handleMarkLeave = async (leaveData) => {
    const user = JSON.parse(localStorage.getItem("user"));
    const userId = user?.result[0]?.user.id;

    // Get the employeePlanId from the events
    const employeePlanId = leaveData?.events?.employeePlanId || planId || 0;
    
    // Determine if we're working with visit profiles
    const hasVisitProfile = leaveData.events?.visitProfile?.visitProfileId;
    
    let payload;
    
    if (hasVisitProfile) {
        // Has visit profile - collect ALL visit profiles for this date
        const allEventsForDay = Array.isArray(leaveData.events) 
            ? leaveData.events 
            : [leaveData.events];
        
        const visitProfilesData = allEventsForDay
            .filter(event => event?.visitProfile?.visitProfileId)
            .map(event => ({
                visitProfileId: event.visitProfile.visitProfileId,
                planDetails: [] // Empty when on leave
            }));

        payload = {
            employeePlanId: employeePlanId,
            employeeId: userId,
            month: leaveData.day.month,
            year: leaveData.day.year,
            employeePlanDate: {
                employeePlanDateId: leaveData.events?.id || 0,
                planDate: leaveData.events?.planDate || new Date(leaveData.day.year, leaveData.day.month - 1, leaveData.day.day + 1).toISOString(),
                onLeave: true,
                leaveMessage: leaveData?.leaveMessage || "",
                visitProfiles: visitProfilesData,
                planDetails: [] // Empty when using visitProfiles
            }
        };
    } else {
        // No visit profile - use planDetails directly (empty for leave)
        payload = {
            employeePlanId: employeePlanId,
            employeeId: userId,
            month: leaveData.day.month,
            year: leaveData.day.year,
            employeePlanDate: {
                employeePlanDateId: leaveData.events?.id || 0,
                planDate: leaveData.events?.planDate || new Date(leaveData.day.year, leaveData.day.month - 1, leaveData.day.day + 1).toISOString(),
                onLeave: true,
                leaveMessage: leaveData?.leaveMessage || "",
                visitProfiles: [], // Empty array when not using visit profiles
                planDetails: [] // Empty when on leave
            }
        };
    }

    dispatch(updateEmployeePlan(payload));
};

    const handleLeaveIconClick = (e, dayKey) => {
        e.preventDefault();
        e.stopPropagation();
        setShowTooltip(showTooltip === dayKey ? null : dayKey);
    };

    const handleLeaveIconMouseLeave = () => {
        setShowTooltip(null);
    };

    const handleCheckboxChange = (e, day, events) => {
  const dayKey = `${day.year}-${day.month}-${day.day}`;
  
  if (e.target.checked) {
    setSelectedCopyDays(prev => [...prev, { day, events }]);
  } else {
    setSelectedCopyDays(prev => prev.filter(d => {
      const key = `${d.day.year}-${d.day.month}-${d.day.day}`;
      return key !== dayKey;
    }));
  }
};

return (
    <>
        {/* Weekday Headers */}
        <div className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                <div key={index} className="calendar-day-name">
                    <BodySmallMedium text={day} color="#384153" />
                </div>
            ))}
        </div>
        <div className="calendar-days">
            {days.map((day, index) => {

                const events = getEventsForDay(day);
                const showAddButton = getAddButton(day, events);
                
                // For display purposes, if events is an array, merge all planDetails
const displayEvents = (() => {
    if (!events) return null;
    
    // Helper function to check if a plan detail is valid (not empty)
// Helper function to check if a plan detail is valid (not empty)
const isValidPlanDetail = (detail) => {
    return detail && (
        detail.doctorName ||
        detail.practiceLocationName ||
        detail.headOffice ||
        detail.cityName // Show city even if it doesn't have doctor or location
    );
};
    
    if (Array.isArray(events)) {
        // Check for NEW structure (with visitProfiles array)
        const hasNewStructure = events.some(e => e.visitProfiles && e.visitProfiles.length > 0);
        
        if (hasNewStructure) {
            // NEW STRUCTURE: Extract and filter plan details from visitProfiles
            const isOnLeave = events.some(e => e.onLeave);
            const allPlanDetails = isOnLeave ? [] : events
                .flatMap(e => e.visitProfiles?.flatMap(vp => vp.planDetails || []) || [])
                .filter(isValidPlanDetail);
            
            return {
                ...events[0],
                planDetails: allPlanDetails,
                onLeave: isOnLeave,
                leaveMessage: events.find(e => e.onLeave)?.leaveMessage || ""
            };
        } else {
            // OLD STRUCTURE: planDetails at top level
            const isOnLeave = events.some(e => e.onLeave);
            const allPlanDetails = isOnLeave ? [] : events
                .flatMap(e => e?.planDetails || [])
                .filter(isValidPlanDetail);
            
            return {
                ...events[0],
                planDetails: allPlanDetails,
                onLeave: isOnLeave,
                leaveMessage: events.find(e => e.onLeave)?.leaveMessage || ""
            };
        }
    }
    
    // Single event
    if (events.visitProfiles && events.visitProfiles.length > 0) {
        // NEW STRUCTURE: Extract and filter plan details from visitProfiles
        const allPlanDetails = events.visitProfiles
            .flatMap(vp => vp.planDetails || [])
            .filter(isValidPlanDetail);
        
        return {
            ...events,
            planDetails: allPlanDetails
        };
    }
    
    // OLD STRUCTURE or no visit profiles - filter plan details
    if (events.planDetails && events.planDetails.length > 0) {
        return {
            ...events,
            planDetails: events.planDetails.filter(isValidPlanDetail)
        };
    }
    
    return events;
})();
                
                const disablePointer =
                ((myPlan && planStatus !== DRAFT && planStatus !== NEEDSREVISION && displayEvents === undefined) 
                || !day.isCurrentMonth 
                || (!myPlan && displayEvents === undefined) 
                || (myPlan && !permissions.includes('CanViewPlan'))
                || (!myPlan && !displayEvents?.planDetails?.length) 
                || (myPlan && planStatus !== DRAFT && planStatus !== NEEDSREVISION && !displayEvents?.planDetails?.length));
                    const showThreeDots = myPlan && !copyMode && planStatus === APPROVED && 
                    day.isCurrentMonth && 
                    !displayEvents?.onLeave && 
                    displayEvents && 
                    displayEvents.id &&  !isPastDate(day); 
                        const dayKey = `${day.year}-${day.month}-${day.day}`;

                return (
                    <div
                        key={index}
                        className="calendar-day"
                        style={{ cursor: disablePointer ? 'default' : 'pointer', backgroundColor: displayEvents?.onLeave ? "#FFF0E9" : ""}}
                        onClick={() => {
                            if (!disablePointer && day.isCurrentMonth) {
                                handleOnDayClick(day);
                            }
                        }}
                    >
                        <div className="calendar-day-header">
                            <div className="day-number-container">
                                <BodyExtraSmallMedium
                                    text={day.day.toString()}
                                    color={day.isCurrentMonth ? '#384153' : '#A1A2A4'}
                                />
                                {copyMode && day.isCurrentMonth && displayEvents && !displayEvents.onLeave && (
<input
  type="checkbox"
  style={{
  position: "absolute",
  right: "2px",
  width: "18px",
  height: "18px",
  cursor: "pointer",
  zIndex: 5,
  backgroundColor: "#1955FD"
}}
 onClick={(e) => e.stopPropagation()}
  onChange={(e) => handleCheckboxChange(e, day, events)}
/>
)}
                                {displayEvents?.onLeave && (
                                    <div className="leave-info-container">
                                        <BodySmallMedium text='On Leave' color='#F23030' />
                                        <Info
                                            size={16}
                                            className="leave-info-icon"
                                            onClick={(e) => handleLeaveIconClick(e, dayKey)}
                                            onMouseLeave={handleLeaveIconMouseLeave}
                                        />
                                        {showTooltip === dayKey && (
                                            <div className="leave-tooltip">
                                                {displayEvents.leaveMessage}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            {showThreeDots && (
                                <div className="three-dots-menu">
                                    <button
                                        className="three-dots-button"
                                        onClick={(e) => handleThreeDotsClick(e, day, displayEvents)}
                                    >
                                        ⋮
                                    </button>
                                    {showMenu === dayKey && (
                                        <div className="menu-dropdown">
                                            <button
                                                className="menu-option"
                                                onClick={(e) => handleMarkLeaveClick(e, day, displayEvents)}
                                            >
                                                <Calendar size={16} />
                                                <span>Mark Leave</span>
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="event-container">
                            {displayEvents?.planDetails?.slice(0, MAX_VISIBLE_DOCTORS).map((detail, idx) => (
                                <span key={idx} className="event-detail">
                                    {detail.doctorName ? detail.doctorName : detail.practiceLocationName ? detail.practiceLocationName : detail.headOffice ? HEADOFFICE : detail.cityName}
                                </span>
                            ))}
                            {displayEvents?.planDetails && displayEvents.planDetails.length > MAX_VISIBLE_DOCTORS && (
                                <span className="event-detail">
                                    +{displayEvents.planDetails.length - MAX_VISIBLE_DOCTORS} more
                                </span>
                            )}
                        </div>

                        {/*showing plus button on calendar cells where planStatus is in draft or needs revision state*/}
                        {showAddButton && (
                            <button
                            className="add-schedule-button"
                            onClick={(e) => {
                            e.stopPropagation();
                            handleOnDayClick(day);
                            }}
                        >
                            +
                        </button>
)                           }
                    </div>
                );
            })}
        </div>
        <MarkLeaveModal
            isVisible={isMarkLeaveModalVisible}
            onClose={closeMarkLeaveModal}
            selectedDay={selectedDay}
            onMarkLeave={handleMarkLeave}
            isLoading={false}
        />
    </>
);
};

export default CalendarMonthView;